import { Controller } from "@hotwired/stimulus";

/**
 * キャンペーンの掲出物（イチオシ枠、バナー）のクリックを GA4 に送るコントローラー
 *
 * 使用場所:
 * - イチオシ商品コンポーネント（creative_slot: featured）
 * - 商品一覧のバナーカルーセル（creative_slot: banner、スライドごとに1件）
 *
 * 送るイベント:
 * - select_promotion: 掲出物の中のリンクがクリックされたとき
 *
 * 必要なターゲット要素と属性:
 * - promotion: 掲出物。data-promotion-id（キャンペーンID）、data-promotion-name、data-creative-slot、
 *   任意で data-promotion-items（掲出中の商品の JSON。クリックされた商品の情報を引くのに使う）を持つ
 * - 掲出物の中のリンクの data-campaign-link: クリックの種類（campaign_page / product_detail / cart_modal / external）
 *
 * gtag は本番環境でのみ読み込まれる。開発環境では送らずにコンソールへ出力する。
 * dataLayer への直接 push は GTM 側のトリガーと二重計測になるおそれがあるため使わない。
 */
export default class extends Controller {
  static targets = ["promotion"];

  connect() {
    this.handleClick = this.trackSelect.bind(this);
    this.element.addEventListener("click", this.handleClick);
  }

  disconnect() {
    this.element.removeEventListener("click", this.handleClick);
  }

  trackSelect(event) {
    const link = event.target.closest("a");
    if (!link) return;

    const promotion = this.promotionTargets.find((target) => target.contains(link));
    if (!promotion) return;

    const params = {
      ...this.promotionParams(promotion),
      link_type: link.dataset.campaignLink || "other",
    };

    // 商品カードのクリックなら、どの商品が押されたかを items に入れる
    const productCode = link.closest("[data-product-code]")?.dataset.productCode;
    if (productCode) {
      const item = this.promotionItems(promotion).find((i) => i.item_id === productCode);
      params.items = [item || { item_id: productCode }];
    }

    this.sendEvent("select_promotion", params);
  }

  // GA4 は promotion_id や creative_slot を items 側へ移すことがあるため、
  // BigQuery エクスポートの event_params から確実に引けるよう campaign_id と placement を独自パラメータとして重ねて送る
  promotionParams(promotion) {
    const { promotionId, promotionName, creativeSlot } = promotion.dataset;
    return {
      promotion_id: promotionId,
      promotion_name: promotionName,
      creative_slot: creativeSlot,
      campaign_id: promotionId,
      placement: creativeSlot,
    };
  }

  promotionItems(promotion) {
    const { promotionItems } = promotion.dataset;
    if (!promotionItems) return [];

    try {
      return JSON.parse(promotionItems);
    } catch {
      return [];
    }
  }

  sendEvent(name, params) {
    // 同じタブで遷移するリンクでも送信が途切れないよう beacon で送る
    const eventParams = { ...params, transport_type: "beacon" };

    if (typeof gtag === "function") {
      gtag("event", name, eventParams);
    } else if (import.meta.env.DEV) {
      console.debug("[GA4]", name, eventParams);
    }
  }
}
